import { Typography, message, Input, Row, Col, Card, Button, Space, Tooltip, Dropdown, Menu } from "antd";
import { TeamOutlined, PlusOutlined, ExportOutlined, MoreOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import PersonasIndex from "./PersonasIndex";
import axios from "axios";
import { useAuth } from "../../layout/auth";
import PersonaForm from "../verifications/forms/persona/PersonaForm";

const CARD = {
  background: "#fff",
  border: "0.5px solid rgba(0,0,0,0.08)",
  borderRadius: "12px",
};

export default function Persons() {
  const [personas, setPersonas] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredPersonas, setFilteredPersonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [personaFormVisible, setPersonaFormVisible] = useState(false);

  const auth = useAuth();

  const fetchPersonas = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/personas/view/list`)
      .then((res) => {
        setPersonas(res.data);
        setFilteredPersonas(res.data);
        setCurrentData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPersonas();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = personas.filter((persona) => {
        return persona?.NombreCompleto?.toLowerCase().includes(search.toLowerCase());
      });
      setFilteredPersonas(filtered);
      setCurrentData(filtered);
    } else {
      setFilteredPersonas(personas);
      setCurrentData(personas);
    }
  }, [search, personas]);

  const handleExport = () => {
    if (!currentData?.length) return message.warning("No hay datos para exportar");

    const headers = ["Nombre Completo", "Tipo de Persona", "Participaciones", "Nacionalidad", "Teléfonos", "Correos", "Datos Adicionales"];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return "";
      const s = String(val);
      if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const rows = currentData.map((v) => [
      escapeCSV(v.NombreCompleto),
      escapeCSV(v.TipoPersona),
      escapeCSV(v.Participaciones),
      escapeCSV(v.Nacionalidad),
      escapeCSV(v.Telefonos),
      escapeCSV(v.Correos),
      escapeCSV(v.Datos),
    ]);

    const csvContent = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `personas_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success("Exportado correctamente");
  };

  const mobileMenuItems = [
    {
      key: "exportar",
      label: "Exportar",
      icon: <ExportOutlined />,
      onClick: handleExport,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {/* ── Header card ── */}
      <div style={{ ...CARD, padding: 0 }}>
        {/* Title row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            padding: "18px 20px 14px",
            borderBottom: "0.5px solid rgba(0,0,0,0.06)",
            flexWrap: "wrap",
          }}
        >
          {/* Icon badge */}
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: "rgba(24,144,255,0.08)",
              border: "0.5px solid rgba(24,144,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <TeamOutlined style={{ fontSize: 17, color: "#1677ff" }} />
          </div>

          {/* Title + subtitle */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "rgba(0,0,0,0.88)", lineHeight: 1.3 }}>Personas</div>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.38)", marginTop: 1 }}>Administración de personas físicas y morales</div>
          </div>

          {/* Desktop actions */}
          <Space size={8} className="vf-desktop-actions" style={{ flexShrink: 0 }}>
            <Tooltip title="Agregar persona">
              <Button type="primary" icon={<PlusOutlined />} style={{ fontSize: 13, fontWeight: 500 }} onClick={() => setPersonaFormVisible(true)}>
                Agregar
              </Button>
            </Tooltip>
            <Tooltip title="Exportar como CSV">
              <Button icon={<ExportOutlined />} style={{ fontSize: 13 }} onClick={handleExport}>
                Exportar
              </Button>
            </Tooltip>
          </Space>

          {/* Mobile actions */}
          <Space size={8} className="vf-mobile-actions" style={{ flexShrink: 0 }}>
            <Button type="primary" icon={<PlusOutlined />} style={{ fontSize: 13, fontWeight: 500 }} onClick={() => setPersonaFormVisible(true)}>
              Agregar
            </Button>
            <Dropdown overlay={<Menu items={mobileMenuItems} />} trigger={["click"]} placement="bottomRight">
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        </div>

        {/* Search row */}
        <div style={{ padding: "12px 20px" }}>
          <Input.Search
            placeholder="Buscar por nombre…"
            onSearch={(value) => setSearch(value)}
            onChange={(e) => {
              if (!e.target.value) setSearch("");
            }}
            allowClear
            loading={loading}
            style={{ maxWidth: 380 }}
          />
        </div>
      </div>

      {/* ── Table card ── */}
      <div style={{ ...CARD, overflow: "hidden" }}>
        <PersonasIndex personas={filteredPersonas} loading={loading} onDataChange={setCurrentData} />
      </div>

      {/* Responsive */}
      <style>{`
        .vf-mobile-actions  { display: none !important; }
        .vf-desktop-actions { display: flex !important; }
        @media (max-width: 600px) {
          .vf-mobile-actions  { display: flex !important; }
          .vf-desktop-actions { display: none !important; }
        }
      `}</style>

      <PersonaForm
        personaFormVisible={personaFormVisible}
        setPersonaFormVisible={setPersonaFormVisible}
        setPersonasList={setPersonas}
        // Other props needed but null for "add"
        setDatosPersona={() => {}}
        setTelefonosFijos={() => {}}
        setTelefonosMoviles={() => {}}
        setCorreosPersona={() => {}}
      />
    </div>
  );
}
